import React, { useState, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { ShoppingCart, Menu, X, Search } from 'lucide-react';
import companyLogo from '../assets/logo.webp';

const Header = ({ onCartClick }) => {
  const { cartTotal, totalItems } = useCart();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchTerm)}`);
    }
  };

  return (
    <header className="bg-white text-gray-800 shadow-md sticky top-0 z-50">
      <div className="container mx-auto flex items-center justify-between p-4">
        {/* Mobile & Tablet Header layout */}
        <div className="flex items-center md:hidden w-full">
          <button onClick={() => setIsMobileMenuOpen(true)} className="p-2 text-gray-800 hover:text-red-600">
            <Menu size={24} />
          </button>
          <Link to="/" className="flex items-center space-x-2 flex-grow justify-center">
            <img src={companyLogo} alt="MM Fireworks Logo" className="h-10" />
            <h1 className="text-xl font-bold">MM Fireworks</h1>
          </Link>
          <button onClick={onCartClick} className="flex items-center space-x-1">
            <div className="relative p-2 bg-red-100 rounded-full">
              <ShoppingCart size={24} className="text-red-600" />
              {totalItems > 0 && (
                <span className="absolute top-0 right-0 bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center -mt-1 -mr-1 animate-pulse">
                  {totalItems}
                </span>
              )}
            </div>
          </button>
        </div>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center justify-between w-full">
          <Link to="/" className="flex items-center space-x-2">
            <img src={companyLogo} alt="MM Fireworks Logo" className="h-12" />
            <h1 className="text-xl font-bold">MM Fireworks</h1>
          </Link>
          <nav className="flex items-center space-x-6 font-semibold">
            <Link to="/" className="hover:text-red-600 transition-colors duration-200">Home</Link>
            <Link to="/products" className="hover:text-red-600 transition-colors duration-200">Shop</Link>
            
            {/* Quick Buy link with badge */}
            <Link to="/quick-buy" className="relative hover:text-red-600 transition-colors duration-200">
              Quick Buy
              <span className="absolute top-0 right-[-25px] inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold leading-none text-yellow-800 bg-yellow-200 rounded-full animate-pulse">
                New
              </span>
            </Link>
            <Link to="/quotation" className="hover:text-red-600 transition-colors duration-200">Quotation</Link>
            <Link to="/about-us" className="hover:text-red-600 transition-colors duration-200">About Us</Link>
            <Link to="/contact-us" className="hover:text-red-600 transition-colors duration-200">Contact Us</Link>
          </nav>
          <div className="flex items-center space-x-4">
            <form onSubmit={handleSearch} className="relative hidden md:block">
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200"
              />
              <button type="submit" className="absolute right-0 top-0 mt-2 mr-2 p-1 text-gray-500 hover:text-red-600">
                <Search size={20} />
              </button>
            </form>
            <button onClick={onCartClick} className="flex items-center space-x-1 p-2 border-2 border-red-600 rounded-full hover:bg-red-50 transition-colors duration-200">
              <ShoppingCart size={24} className="text-red-600" />
              <span className="text-lg font-bold text-red-600 hidden sm:block">₹{cartTotal.toFixed(2)}</span>
              {totalItems > 0 && (
                <span className="bg-red-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile/Tablet Side Drawer */}
      <div className={`fixed inset-y-0 left-0 w-64 bg-white shadow-xl transform transition-transform ease-in-out duration-300 z-50 md:hidden ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-800">Menu</h2>
          <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-gray-800 hover:text-red-600">
            <X size={24} />
          </button>
        </div>
        <form onSubmit={handleSearch} className="p-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500 transition-all duration-200"
            />
            <button type="submit" className="absolute right-0 top-0 mt-2 mr-2 p-1 text-gray-500 hover:text-red-600">
              <Search size={20} />
            </button>
          </div>
        </form>
        <nav className="flex flex-col p-4 space-y-2 font-semibold text-lg">
          <Link to="/" className="p-2 hover:bg-gray-100 rounded-lg" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
          <Link to="/products" className="p-2 hover:bg-gray-100 rounded-lg" onClick={() => setIsMobileMenuOpen(false)}>Shop</Link>
          
          {/* Quick Buy link with badge in mobile menu */}
          <Link to="/quick-buy" className="relative p-2 bg-yellow-100 text-yellow-800 rounded-lg hover:bg-yellow-200 transition-colors duration-200" onClick={() => setIsMobileMenuOpen(false)}>
            Quick Buy
            <span className="absolute top-1/2 right-4 -translate-y-1/2 inline-flex items-center px-2 py-0.5 text-xs font-bold leading-none text-yellow-800 bg-yellow-200 rounded-full animate-pulse">
              New
            </span>
          </Link>
          <Link to="/quotation" className="p-2 hover:bg-gray-100 rounded-lg" onClick={() => setIsMobileMenuOpen(false)}>Quotation</Link>
          <Link to="/about-us" className="p-2 hover:bg-gray-100 rounded-lg" onClick={() => setIsMobileMenuOpen(false)}>About Us</Link>
          <Link to="/contact-us" className="p-2 hover:bg-gray-100 rounded-lg" onClick={() => setIsMobileMenuOpen(false)}>Contact Us</Link>
        </nav>
      </div>

      {isMobileMenuOpen && (
        <div onClick={() => setIsMobileMenuOpen(false)} className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"></div>
      )}
    </header>
  );
};

export default memo(Header);